/* eslint-disable react/no-unstable-nested-components */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionContext";
import { useTheme } from "@/context/ThemeContext";
import { getEBCIWeather, getLvoStatus, respondToLvo, manualLvoToggle, getPendingFlights, getLiveTracking } from "@/lib/api";
import AIAssistant from "@/components/AIAssistant";
import { toast } from "sonner";
import { 
  LayoutDashboard, 
  Plane, 
  Calendar, 
  BarChart3, 
  Users, 
  History, 
  Settings, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  XCircle,
  Database,
  MapPin,
  FileUp,
  Clock,
  Wind,
  AlertTriangle,
  CheckCircle,
  Shield,
  Moon,
  Sun,
  Crown,
  ShieldCheck,
  UserCog,
  ParkingCircle,
  Wrench,
  Snowflake,
  Eye,
  FileText,
  BookOpen,
  Mail,
  Inbox,
  Send,
  Activity,
  Package,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import DemoModeDialog from "@/components/DemoModeDialog";

// Role display configuration
const ROLE_DISPLAY = {
  master_admin: { label: "Master Admin", icon: Crown, color: "text-yellow-400" },
  admin: { label: "Admin", icon: ShieldCheck, color: "text-blue-400" },
  operational_agent: { label: "Operational Agent", icon: UserCog, color: "text-green-400" },
  exposition: { label: "Exposition (Demo)", icon: Eye, color: "text-amber-400" },
  staff: { label: "Operational Agent", icon: UserCog, color: "text-green-400" }
};

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, section: "dashboard" },
  { path: "/global-planning", label: "Global Planning", icon: Calendar, section: "global_planning" },
  { path: "/daily-plannings", label: "Daily Plannings", icon: Send, section: null },
  { path: "/apron-management", label: "Apron Management", icon: ParkingCircle, section: "global_planning" },
  { path: "/flights", label: "Flights", icon: Plane, section: "flights" },
  { path: "/email-pending", label: "Email Requests", icon: Inbox, section: null },
  { path: "/canceled-flights", label: "Canceled Flights", icon: XCircle, section: "canceled_flights" },
  { path: "/statistics", label: "Statistics", icon: BarChart3, section: "statistics" },
  { path: "/services", label: "Services", icon: Wrench, section: "services" },
  { path: "/aip-info", label: "AIP Info", icon: FileText, section: null },
  { path: "/procedures", label: "Procedures", icon: BookOpen, section: null },
];

const adminNavItems = [
  { path: "/users", label: "User Management", icon: Users, section: "users" },
  { path: "/permissions", label: "Permissions", icon: Shield, section: "permissions" },
  { path: "/financial-statistics", label: "Financial Statistics", icon: TrendingUp, section: "statistics", masterOnly: true },
  { path: "/aircraft-data", label: "Aircraft Data", icon: Database, section: "aircraft_data" },
  { path: "/airport-data", label: "Airport Data", icon: MapPin, section: "airport_data" },
  { path: "/parking-config", label: "Parking Configuration", icon: ParkingCircle, section: "settings", masterOnly: true },
  { path: "/email-settings", label: "Email Settings", icon: Mail, section: "settings", masterOnly: true },
  { path: "/import-export", label: "Import/Export Flights", icon: FileUp, section: "import_export" },
  { path: "/login-history", label: "Login History", icon: History, section: "login_history" },
  { path: "/system-health", label: "System Health", icon: Activity, section: "login_history" },
  { path: "/settings", label: "Settings", icon: Settings, section: "settings" },
];

export const Layout = ({ children }) => {
  const { user, logout, settings } = useAuth();
  const { hasPermission, isMasterAdmin, isExposition } = usePermissions();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [weatherData, setWeatherData] = useState(null);
  const [lvoStatus, setLvoStatus] = useState(null);
  const [showLvoPopup, setShowLvoPopup] = useState(false);
  const [lvoPopupNotification, setLvoPopupNotification] = useState(null);
  const [showLvoManual, setShowLvoManual] = useState(false);
  const [pendingFlightPopup, setPendingFlightPopup] = useState(null);
  const [showFlightPopup, setShowFlightPopup] = useState(false);
  const [knownPendingIds, setKnownPendingIds] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("known_pending_ids") || "[]"); } catch { return []; }
  });
  const [showPwWarning, setShowPwWarning] = useState(false);

  // Password expiry warning popup (shown once per day, within 15 days of expiry)
  useEffect(() => {
    if (user?.password_expiry_warn) {
      const key = `pw_warn_dismissed_${new Date().toISOString().slice(0, 10)}`;
      if (sessionStorage.getItem(key) !== "1") {
        setShowPwWarning(true);
      }
    }
  }, [user]);

  const dismissPwWarning = () => {
    const key = `pw_warn_dismissed_${new Date().toISOString().slice(0, 10)}`;
    sessionStorage.setItem(key, "1");
    setShowPwWarning(false);
  };

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Exposition (demo) users: block opening any file-upload dialog globally
  useEffect(() => {
    if (!isExposition) return;
    const blockFileDialog = (e) => {
      const t = e.target;
      if (t && t.tagName === "INPUT" && t.type === "file") {
        e.preventDefault();
        e.stopPropagation();
        window.dispatchEvent(new CustomEvent("demo-mode-block"));
      }
    };
    document.addEventListener("click", blockFileDialog, true);
    return () => document.removeEventListener("click", blockFileDialog, true);
  }, [isExposition]);

  // Fetch weather data for alerts
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const response = await getEBCIWeather();
        setWeatherData(response.data);
      } catch {
        // weather is optional
      }
    };
    fetchWeather();
    // Refresh every 60 seconds
    const interval = setInterval(fetchWeather, 60000);
    return () => clearInterval(interval);
  }, []);

  // Poll LVO status + auto-dismiss if handled by another user
  const fetchLvoStatus = useCallback(async () => {
    try {
      const res = await getLvoStatus();
      const data = res.data;
      setLvoStatus(data);
      
      // Auto-dismiss LVO popup if the notification is no longer pending
      if (showLvoPopup && lvoPopupNotification) {
        if (!data.pending_notification || 
            data.pending_notification.id !== lvoPopupNotification.id ||
            data.pending_notification.status !== "pending") {
          setShowLvoPopup(false);
          setLvoPopupNotification(null);
          toast.info("This LVO notification was already handled by another user");
          return;
        }
      }
      
      // Show popup if there's a pending notification we haven't seen yet
      if (data.pending_notification && data.pending_notification.status === "pending") {
        const dismissedId = sessionStorage.getItem("lvo_dismissed_id");
        if (dismissedId !== data.pending_notification.id) {
          setLvoPopupNotification(data.pending_notification);
          setShowLvoPopup(true);
        }
      }
    } catch (err) {
      // Silent fail
    }
  }, [showLvoPopup, lvoPopupNotification]);

  useEffect(() => {
    fetchLvoStatus();
    const lvoInterval = setInterval(fetchLvoStatus, 30000);
    return () => clearInterval(lvoInterval);
  }, [fetchLvoStatus]);

  // Poll for new pending flight requests + auto-dismiss if handled by another user
  const fetchPendingFlights = useCallback(async () => {
    try {
      const res = await getPendingFlights();
      const pendingList = res.data || [];
      
      // Auto-dismiss popup if the shown notification is no longer in the pending list
      if (showFlightPopup && pendingFlightPopup) {
        const stillPending = pendingList.find(f => f.id === pendingFlightPopup.id);
        if (!stillPending) {
          // Another user already handled this request
          setShowFlightPopup(false);
          setPendingFlightPopup(null);
          toast.info("This flight request was already handled by another user");
          return;
        }
      }
      
      if (pendingList.length > 0) {
        // Check for any new pending flight not yet seen
        const newFlight = pendingList.find(f => !knownPendingIds.includes(f.id));
        if (newFlight) {
          setPendingFlightPopup(newFlight);
          setShowFlightPopup(true);
        }
      }
    } catch (err) {
      // Silent fail
    }
  }, [knownPendingIds, showFlightPopup, pendingFlightPopup]);

  useEffect(() => {
    fetchPendingFlights();
    const flightInterval = setInterval(fetchPendingFlights, 15000);
    return () => clearInterval(flightInterval);
  }, [fetchPendingFlights]);

  // ===== Arrival landing voice alert (≈5 min before landing) =====
  const announcedArrivalsRef = useRef(null);

  const getAnnouncedKey = () => `announced_arrivals_${new Date().toISOString().slice(0, 10)}`;

  const loadAnnounced = () => {
    if (announcedArrivalsRef.current) return announcedArrivalsRef.current;
    try {
      announcedArrivalsRef.current = new Set(JSON.parse(localStorage.getItem(getAnnouncedKey()) || "[]"));
    } catch {
      announcedArrivalsRef.current = new Set();
    }
    return announcedArrivalsRef.current;
  };

  const speakArrival = (f) => {
    try {
      if (!("speechSynthesis" in window)) return;
      if (localStorage.getItem("announcement_voice_enabled") === "false") return;
      const origin = f.origin_name || f.origin || "an unknown airport";
      const flightId = f.flight_number || f.registration || "";
      const parking = f.parking_position ? `parking position ${f.parking_position}` : "the apron";
      const eta = f.eta ? `, estimated at ${f.eta}` : "";
      const text = `Attention. Flight ${flightId} from ${origin} is approaching for ${parking}${eta}.`;
      const utter = new SpeechSynthesisUtterance(text);
      utter.rate = 0.95;
      utter.pitch = 1.05;
      utter.lang = "en-GB";
      const voices = window.speechSynthesis.getVoices() || [];
      const savedName = localStorage.getItem("announcement_voice_name");
      const chosen =
        (savedName && voices.find((v) => v.name === savedName)) ||
        voices.find((v) => /(female|zira|samantha|hazel|fiona|karen|moira|tessa|susan|serena|google uk english female)/i.test(v.name)) ||
        voices.find((v) => /^en/i.test(v.lang) && /female/i.test(v.name)) ||
        voices.find((v) => /^en-GB/i.test(v.lang)) ||
        voices.find((v) => /^en/i.test(v.lang));
      if (chosen) utter.voice = chosen;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utter);
    } catch (e) {
      // ignore speech errors
    }
  };

  const checkArrivals = useCallback(async () => {
    try {
      const res = await getLiveTracking();
      const flights = res.data?.flights || {};
      const announced = loadAnnounced();
      Object.values(flights).forEach((f) => {
        const isArrival = (f.destination || "").toUpperCase() === "EBCI";
        const status = (f.tracking_status || "").toUpperCase();
        const etaMin = f.eta_minutes;
        const approaching =
          isArrival &&
          (status === "AIRBORNE" || status === "APPROACH") &&
          etaMin != null &&
          etaMin > 0 &&
          etaMin <= 5;
        if (!approaching || announced.has(f.flight_id)) return;

        announced.add(f.flight_id);
        try {
          localStorage.setItem(getAnnouncedKey(), JSON.stringify([...announced]));
        } catch {
          // ignore storage errors
        }

        // Non-blocking toast notification
        toast("✈️ Flight approaching landing", {
          description: `${f.flight_number || f.registration} from ${f.origin_name || f.origin || "?"} • ${
            f.parking_position ? `Stand ${f.parking_position}` : "Apron"
          } • ETA ${f.eta || `${Math.round(etaMin)} min`}`,
          duration: 20000,
          icon: <Plane className="w-4 h-4 text-blue-500" />,
        });
        speakArrival(f);
      });
    } catch (e) {
      // silent fail
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.getVoices();
    }
    checkArrivals();
    const arrivalInterval = setInterval(checkArrivals, 30000);
    return () => clearInterval(arrivalInterval);
  }, [checkArrivals]);

  const dismissFlightPopup = () => {
    if (pendingFlightPopup) {
      const updated = [...knownPendingIds, pendingFlightPopup.id];
      setKnownPendingIds(updated);
      sessionStorage.setItem("known_pending_ids", JSON.stringify(updated));
    }
    setShowFlightPopup(false);
    setPendingFlightPopup(null);
  };

  const openFlightRequests = () => {
    dismissFlightPopup();
    navigate("/email-pending");
  };

  const handleLvoResponse = async (action) => {
    if (!lvoPopupNotification) return;
    try {
      await respondToLvo(lvoPopupNotification.id, action);
      toast.success(action === "accept" 
        ? `LVO ${lvoPopupNotification.lvo_action === "activate" ? "activated" : "deactivated"}`
        : "LVO notification declined"
      );
      setShowLvoPopup(false);
      setLvoPopupNotification(null);
      fetchLvoStatus();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.warning(err.response.data.detail || "Already handled by another user");
        setShowLvoPopup(false);
        setLvoPopupNotification(null);
        fetchLvoStatus();
      } else {
        toast.error("Failed to respond to LVO notification");
      }
    }
  };

  const dismissLvoPopup = () => {
    if (lvoPopupNotification) {
      sessionStorage.setItem("lvo_dismissed_id", lvoPopupNotification.id);
    }
    setShowLvoPopup(false);
  };

  const handleManualLvoToggle = async (activate) => {
    try {
      await manualLvoToggle(activate, activate ? "MANUAL" : null);
      toast.success(activate ? "LVO manually activated" : "LVO deactivated");
      setShowLvoManual(false);
      fetchLvoStatus();
    } catch (err) {
      toast.error("Failed to toggle LVO");
    }
  };

  // Format times
  const utcTime = currentTime.toUTCString().slice(17, 25);
  const brusselsTime = currentTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Brussels' });

  // Weather alerts logic - LVO, Wind, De-Ice
  const getWeatherAlerts = () => {
    if (!weatherData?.metar) return { 
      lvo: { status: 'normal', message: 'LVO / Normal Situation' },
      wind: { status: 'normal', message: 'Wind / Normal Situation', speed: 0 },
      deice: { status: 'normal', message: 'De-Ice / Normal Situation' },
      isLoading: true 
    };
    
    const metar = weatherData.metar;
    const rawMetar = metar.rawOb || '';
    const windSpeed = metar.wspd || 0;
    const gustSpeed = metar.wgst || 0;
    const maxWind = Math.max(windSpeed, gustSpeed);
    const visibility = metar.visib; // in statute miles from METAR
    const tempC = metar.temp; // temperature in Celsius
    
    // LVO - Check email-based LVO status first, then weather-based
    const visibilityKm = visibility ? visibility * 1.60934 : 999;
    const hasLowVisibility = visibilityKm < 1.5 || rawMetar.includes('FG') || rawMetar.includes('FZFG');
    
    let lvo;
    if (lvoStatus?.lvo_active) {
      // Email-confirmed LVO is active
      const cat = lvoStatus.lvo_category ? ` ${lvoStatus.lvo_category}` : '';
      lvo = { status: 'warning', message: `LVO${cat} / ACTIVE` };
    } else if (hasLowVisibility) {
      lvo = { status: 'warning', message: 'LVO / Possible LVO Operations' };
    } else {
      lvo = { status: 'normal', message: 'LVO / Normal Situation' };
    }
    
    // Wind Force warnings
    // 25-40 kt: Secure Aircraft
    // 41-60 kt: Secure Aircraft + Move GSE
    // >61 kt: Operations Canceled
    let wind = { status: 'normal', message: 'Wind / Normal Situation', speed: maxWind };
    if (maxWind >= 61) {
      wind = { status: 'critical', message: `Wind ${maxWind}kt / Stby All Operations`, speed: maxWind };
    } else if (maxWind >= 41) {
      wind = { status: 'warning', message: `Wind ${maxWind}kt / Secure Aircraft + Move GSE`, speed: maxWind };
    } else if (maxWind >= 25) {
      wind = { status: 'warning', message: `Wind ${maxWind}kt / Secure Aircraft`, speed: maxWind };
    }
    
    // De-Icing - Temperature < 1°C
    const needsDeicing = tempC !== undefined && tempC < 1;
    const deice = needsDeicing
      ? { status: 'warning', message: `De-Ice / Possible De-Icing (${tempC}°C)` }
      : { status: 'normal', message: 'De-Ice / Normal Situation' };
    
    return { lvo, wind, deice, isLoading: false };
  };

  const alerts = getWeatherAlerts();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Parse company name to get short version for sidebar
  const getShortCompanyName = () => {
    if (!settings.company_name) return "CSB Aviation";
    const name = settings.company_name;
    if (name.length <= 20) return name;
    // Try to get first word or abbreviation
    const words = name.split(' ');
    if (words.length >= 2) {
      return words.slice(0, 2).join(' ');
    }
    return name.substring(0, 20) + '...';
  };

  const NavItem = ({ item, onClick }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    
    return (
      <button
        onClick={() => {
          navigate(item.path);
          onClick?.();
        }}
        className={`nav-item w-full text-left ${isActive ? "active" : ""}`}
        data-testid={`nav-${item.path.replace("/", "")}`}
      >
        <Icon size={20} />
        <span>{item.label}</span>
        {isActive && <ChevronRight size={16} className="ml-auto" />}
      </button>
    );
  };

  const SidebarContent = ({ onItemClick }) => {
    // Filter nav items based on permissions (items with no section are always visible)
    const visibleNavItems = navItems.filter(item => !item.section || hasPermission(item.section, "view"));
    const visibleAdminItems = adminNavItems.filter(item => 
      hasPermission(item.section, "view") && (!item.masterOnly || isMasterAdmin || isExposition)
    );

    // State for collapsible sections - accordion style (only one open at a time)
    // Default: Main Menu open, Administration closed
    const [mainMenuOpen, setMainMenuOpen] = useState(() => {
      const saved = localStorage.getItem('sidebar_mainMenu_open');
      return saved !== null ? JSON.parse(saved) : true; // Default: open
    });
    const [adminMenuOpen, setAdminMenuOpen] = useState(() => {
      const saved = localStorage.getItem('sidebar_adminMenu_open');
      return saved !== null ? JSON.parse(saved) : false; // Default: closed
    });

    // Accordion toggle handlers - when one opens, the other closes
    const toggleMainMenu = () => {
      if (!mainMenuOpen) {
        setMainMenuOpen(true);
        setAdminMenuOpen(false);
      } else {
        setMainMenuOpen(false);
      }
    };

    const toggleAdminMenu = () => {
      if (!adminMenuOpen) {
        setAdminMenuOpen(true);
        setMainMenuOpen(false);
      } else {
        setAdminMenuOpen(false);
      }
    };

    // Persist section states
    useEffect(() => {
      localStorage.setItem('sidebar_mainMenu_open', JSON.stringify(mainMenuOpen));
    }, [mainMenuOpen]);

    useEffect(() => {
      localStorage.setItem('sidebar_adminMenu_open', JSON.stringify(adminMenuOpen));
    }, [adminMenuOpen]);
    
    return (
      <>
        {/* Collapsible Menu Animation Styles */}
        <style>{`
          .menu-section-content {
            overflow: hidden;
            transition: max-height 0.3s ease-out, opacity 0.2s ease-out;
          }
          .menu-section-content.collapsed {
            max-height: 0 !important;
            opacity: 0;
          }
          .menu-section-content.expanded {
            opacity: 1;
          }
          .menu-section-header {
            cursor: pointer;
            user-select: none;
            transition: background-color 0.2s ease;
          }
          .menu-section-header:hover {
            background-color: rgba(255, 255, 255, 0.05);
          }
          .menu-chevron {
            transition: transform 0.3s ease;
          }
          .menu-chevron.rotated {
            transform: rotate(-90deg);
          }
        `}</style>

        {/* Logo Section */}
        <div className="logo-container">
          {settings.logo_base64 ? (
            <img src={settings.logo_base64} alt="Company Logo" className="max-h-12 max-w-[180px] object-contain" />
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#d4af37] rounded-lg flex items-center justify-center">
                <Plane size={24} className="text-[#0f172a]" />
              </div>
              <div className="text-white">
                <p className="font-semibold text-sm leading-tight">{getShortCompanyName()}</p>
                <p className="text-xs text-gray-400">Business Aviation</p>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {/* Main Menu Section - Collapsible */}
          <div 
            className="menu-section-header flex items-center justify-between px-4 py-2 rounded-lg"
            onClick={toggleMainMenu}
            data-testid="main-menu-toggle"
          >
            <p className="text-xs uppercase text-gray-500 font-semibold">Main Menu</p>
            <ChevronDown 
              size={16} 
              className={`menu-chevron text-gray-500 ${!mainMenuOpen ? 'rotated' : ''}`} 
            />
          </div>
          <div 
            className={`menu-section-content ${mainMenuOpen ? 'expanded' : 'collapsed'}`}
            style={{ maxHeight: mainMenuOpen ? `${visibleNavItems.length * 52 + 20}px` : '0' }}
          >
            {visibleNavItems.map((item) => (
              <NavItem key={item.path} item={item} onClick={onItemClick} />
            ))}
          </div>

          {visibleAdminItems.length > 0 && (
            <>
              <div className="my-3 border-t border-gray-700" />
              
              {/* Administration Section - Collapsible */}
              <div 
                className="menu-section-header flex items-center justify-between px-4 py-2 rounded-lg"
                onClick={toggleAdminMenu}
                data-testid="admin-menu-toggle"
              >
                <p className="text-xs uppercase text-gray-500 font-semibold">Administration</p>
                <ChevronDown 
                  size={16} 
                  className={`menu-chevron text-gray-500 ${!adminMenuOpen ? 'rotated' : ''}`} 
                />
              </div>
              <div 
                className={`menu-section-content ${adminMenuOpen ? 'expanded' : 'collapsed'}`}
                style={{ maxHeight: adminMenuOpen ? `${visibleAdminItems.length * 52 + 20}px` : '0' }}
              >
                {visibleAdminItems.map((item) => (
                  <NavItem key={item.path} item={item} onClick={onItemClick} />
                ))}
              </div>
            </>
          )}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-gray-700">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              user?.role === 'master_admin' ? 'bg-yellow-500' : 
              user?.role === 'admin' ? 'bg-blue-500' : 'bg-[#d4af37]'
            }`}>
              <span className="text-white font-bold">
                {user?.full_name?.charAt(0) || "U"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.full_name}</p>
              <p className={`text-xs flex items-center gap-1 ${ROLE_DISPLAY[user?.role]?.color || 'text-gray-400'}`}>
                {(() => {
                  const RoleIcon = ROLE_DISPLAY[user?.role]?.icon;
                  return RoleIcon ? <RoleIcon size={12} /> : null;
                })()}
                {ROLE_DISPLAY[user?.role]?.label || user?.role}
              </p>
              {isExposition && (
                <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#0f172a]" data-testid="demo-mode-badge">
                  <Eye size={10} /> Demo Mode
                </span>
              )}
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/10"
            data-testid="logout-btn"
          >
            <LogOut size={18} className="mr-2" />
            Logout
          </Button>
        </div>
      </>
    );
  };

  return (
    <div className="app-container">
      {/* Fixed Top Status Bar - Visible on all pages */}
      <div className="fixed top-0 left-0 right-0 h-14 bg-[#0f172a] z-[60] flex items-center justify-between gap-2 px-4 md:pl-72 border-b border-[#1e293b]">
        {/* Weather Alerts - 3 Flashing Badges */}
        <div className="flex items-center gap-1.5 md:gap-2 min-w-0 overflow-x-auto no-scrollbar">
          {/* CSS for weather badge animations */}
          <style>{`
            .weather-badge-base {
              display: flex;
              align-items: center;
              gap: 0.5rem;
              padding: 0.375rem 0.75rem;
              border-radius: 0.5rem;
              font-size: 0.875rem;
              font-weight: 700;
              white-space: nowrap;
            }
            @media (max-width: 767px) {
              .weather-badge-base {
                font-size: 0.625rem;
                padding: 0.25rem 0.4rem;
                gap: 0.25rem;
              }
            }
            .no-scrollbar::-webkit-scrollbar { display: none; }
            .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            .weather-badge-normal {
              background-color: transparent;
              color: #60a5fa;
              border: 1px solid #60a5fa;
            }
            .weather-badge-normal span,
            .weather-badge-normal svg {
              color: inherit;
            }
            .weather-badge-warning {
              animation: weatherFlash 0.6s steps(1) infinite;
              border: 1px solid #dc2626;
            }
            .weather-badge-critical {
              animation: weatherFlash 0.4s steps(1) infinite;
              border: 2px solid #dc2626;
            }
            /* Force child elements to inherit animated color */
            .weather-badge-warning span,
            .weather-badge-warning svg,
            .weather-badge-critical span,
            .weather-badge-critical svg {
              color: inherit !important;
            }
            @keyframes weatherFlash {
              0%, 49% { 
                background-color: #dc2626; 
                color: #ffffff;
              }
              50%, 100% { 
                background-color: #ffffff; 
                color: #dc2626;
              }
            }
          `}</style>
          {alerts.isLoading ? (
            <span className="text-gray-400 text-sm">Loading weather...</span>
          ) : (
            <>
              {/* LVO Badge - Clickable for manual toggle */}
              <div className="relative">
                <div 
                  className={`weather-badge-base cursor-pointer ${
                    alerts.lvo.status === 'warning' 
                      ? 'weather-badge-warning' 
                      : 'weather-badge-normal'
                  }`}
                  data-testid="lvo-alert"
                  onClick={() => setShowLvoManual(!showLvoManual)}
                  title="Click to manually activate/deactivate LVO"
                >
                  <AlertTriangle size={14} />
                  <span className="md:hidden">{alerts.lvo.message.split('/')[0].trim()}</span>
                  <span className="hidden md:inline">{alerts.lvo.message}</span>
                </div>
                
                {/* Manual LVO Toggle Dropdown */}
                {showLvoManual && (
                  <>
                    <div className="fixed inset-0 z-[70]" onClick={() => setShowLvoManual(false)} />
                    <div className="absolute top-full left-0 mt-1 z-[80] bg-[#0f172a] border border-gray-600 rounded-lg shadow-xl p-3 min-w-[220px]" data-testid="lvo-manual-dropdown">
                      <p className="text-gray-400 text-xs mb-2 font-semibold uppercase">Manual LVO Control</p>
                      {lvoStatus?.lvo_active ? (
                        <Button
                          onClick={() => handleManualLvoToggle(false)}
                          className="w-full bg-green-600 hover:bg-green-700 text-white text-sm"
                          data-testid="lvo-manual-deactivate"
                        >
                          <CheckCircle size={14} className="mr-2" /> Deactivate LVO
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleManualLvoToggle(true)}
                          className="w-full bg-red-600 hover:bg-red-700 text-white text-sm"
                          data-testid="lvo-manual-activate"
                        >
                          <AlertTriangle size={14} className="mr-2" /> Activate LVO
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
              
              {/* Wind Badge */}
              <div 
                className={`weather-badge-base ${
                  alerts.wind.status === 'critical' 
                    ? 'weather-badge-critical'
                    : alerts.wind.status === 'warning' 
                      ? 'weather-badge-warning' 
                      : 'weather-badge-normal'
                }`}
                data-testid="wind-alert"
              >
                <Wind size={14} />
                <span className="md:hidden">{alerts.wind.message.split('/')[0].trim()}</span>
                <span className="hidden md:inline">{alerts.wind.message}</span>
              </div>
              
              {/* De-Ice Badge */}
              <div 
                className={`weather-badge-base ${
                  alerts.deice.status === 'warning' 
                    ? 'weather-badge-warning' 
                    : 'weather-badge-normal'
                }`}
                data-testid="deice-alert"
              >
                <Snowflake size={14} />
                <span className="md:hidden">{alerts.deice.message.split('/')[0].trim()}</span>
                <span className="hidden md:inline">{alerts.deice.message}</span>
              </div>
            </>
          )}
        </div>

        {/* Time Display */}
        <div className="flex items-center gap-2 md:gap-8 flex-shrink-0">
          {(isMasterAdmin || hasPermission("cargo", "view")) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open("/cargo/dashboard", "_blank", "noopener")}
              className="text-gray-300 hover:text-emerald-400 hover:bg-[#1e293b] p-2 gap-1.5"
              data-testid="switch-to-cargo-btn"
              title="Open Cargo module in a new tab"
            >
              <Package size={18} />
              <span className="hidden lg:inline text-xs font-semibold">Cargo</span>
            </Button>
          )}
          {/* Dark Mode Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleTheme}
            className="text-gray-400 hover:text-[#d4af37] hover:bg-[#1e293b] p-2"
            data-testid="theme-toggle"
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </Button>
          
          <div className="flex items-center gap-1 md:gap-2">
            <Clock size={16} className="text-[#d4af37] flex-shrink-0" />
            <span className="text-[#d4af37] font-mono font-bold text-sm md:text-base">{utcTime}</span>
            <span className="text-gray-400 text-xs md:text-sm">UTC</span>
          </div>
          <div className="hidden sm:flex items-center gap-1 md:gap-2">
            <Clock size={16} className="text-blue-400 flex-shrink-0" />
            <span className="text-blue-400 font-mono font-bold text-sm md:text-base">{brusselsTime}</span>
            <span className="text-gray-400 text-sm hidden md:inline">Brussels</span>
          </div>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <aside className="sidebar hidden md:flex flex-col w-64">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-14 left-0 right-0 h-16 bg-[#020617] z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          {settings.logo_base64 ? (
            <img src={settings.logo_base64} alt="Logo" className="h-8 object-contain" />
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#d4af37] rounded-lg flex items-center justify-center">
                <Plane size={18} className="text-[#0f172a]" />
              </div>
              <span className="text-white font-semibold text-sm">{getShortCompanyName()}</span>
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(true)}
          className="text-white"
          data-testid="mobile-menu-btn"
        >
          <Menu size={24} />
        </Button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside className="sidebar absolute left-0 top-0 bottom-0 w-64 flex flex-col animate-slide-in">
            <div className="absolute top-4 right-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(false)}
                className="text-white"
              >
                <X size={24} />
              </Button>
            </div>
            <SidebarContent onItemClick={() => setMobileMenuOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="main-content">
        {children}
      </main>

      {/* AI Aviation Assistant - Floating on all pages */}
      <AIAssistant />

      {/* LVO Notification Popup */}
      {showLvoPopup && lvoPopupNotification && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="lvo-popup-overlay">
          <div className="bg-[#0f172a] border-2 border-yellow-500 rounded-xl shadow-2xl shadow-yellow-500/20 max-w-md w-full mx-4 overflow-hidden animate-in fade-in zoom-in" data-testid="lvo-popup">
            {/* Header */}
            <div className={`p-4 ${lvoPopupNotification.lvo_action === "activate" ? "bg-red-600" : "bg-green-600"}`}>
              <div className="flex items-center gap-3">
                <AlertTriangle size={28} className="text-white" />
                <div>
                  <h2 className="text-white font-bold text-lg">
                    LVO {lvoPopupNotification.lvo_action === "activate" ? "ACTIVATION" : "DEACTIVATION"}
                  </h2>
                  <p className="text-white/80 text-sm">Low Visibility Operations Alert</p>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-5 space-y-3">
              <div className="bg-[#020617] border border-gray-700 rounded-lg p-3">
                <p className="text-white text-sm font-medium">{lvoPopupNotification.summary}</p>
              </div>
              
              {lvoPopupNotification.lvo_category && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 text-sm">Category:</span>
                  <span className="text-yellow-400 font-mono font-bold">{lvoPopupNotification.lvo_category}</span>
                </div>
              )}
              {lvoPopupNotification.visibility_info && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 text-sm">Visibility:</span>
                  <span className="text-white text-sm">{lvoPopupNotification.visibility_info}</span>
                </div>
              )}
              {lvoPopupNotification.effective_time && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 text-sm">Effective:</span>
                  <span className="text-white text-sm">{lvoPopupNotification.effective_time} UTC</span>
                </div>
              )}
              
              <p className="text-gray-500 text-xs">From: {lvoPopupNotification.email_sender}</p>
            </div>
            
            {/* Actions */}
            <div className="p-4 border-t border-gray-700 flex items-center gap-3">
              <Button 
                onClick={() => handleLvoResponse("accept")}
                className={`flex-1 ${lvoPopupNotification.lvo_action === "activate" ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"} text-white font-bold`}
                data-testid="lvo-accept-btn"
              >
                <CheckCircle size={18} className="mr-2" />
                Accept & {lvoPopupNotification.lvo_action === "activate" ? "Activate" : "Deactivate"} LVO
              </Button>
              <Button 
                onClick={() => handleLvoResponse("decline")}
                variant="outline"
                className="border-gray-600 text-gray-400 hover:bg-gray-800"
                data-testid="lvo-decline-btn"
              >
                Decline
              </Button>
              <Button 
                onClick={dismissLvoPopup}
                variant="ghost"
                size="sm"
                className="text-gray-500"
                data-testid="lvo-dismiss-btn"
              >
                <X size={16} />
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Flight Request Popup */}
      {showFlightPopup && pendingFlightPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="flight-popup-overlay">
          <div className="bg-[#0f172a] border-2 border-blue-500 rounded-xl shadow-2xl shadow-blue-500/20 max-w-md w-full mx-4 overflow-hidden animate-in fade-in zoom-in" data-testid="flight-popup">
            {/* Header */}
            <div className="p-4 bg-blue-600">
              <div className="flex items-center gap-3">
                <Plane size={28} className="text-white" />
                <div>
                  <h2 className="text-white font-bold text-lg">New Flight Request</h2>
                  <p className="text-white/80 text-sm">From email</p>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-5 space-y-3">
              <div className="bg-[#020617] border border-gray-700 rounded-lg p-3">
                <p className="text-white text-sm font-medium">{pendingFlightPopup.email_subject}</p>
                <p className="text-gray-500 text-xs mt-1">From: {pendingFlightPopup.email_sender}</p>
              </div>
              
              {pendingFlightPopup.parsed_data && (
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {pendingFlightPopup.parsed_data.registration && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">Registration:</span>
                      <span className="text-[#d4af37] font-mono font-bold">{pendingFlightPopup.parsed_data.registration}</span>
                    </div>
                  )}
                  {pendingFlightPopup.parsed_data.aircraft_type && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">Aircraft:</span>
                      <span className="text-white">{pendingFlightPopup.parsed_data.aircraft_type}</span>
                    </div>
                  )}
                  {pendingFlightPopup.parsed_data.flight_date && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">Date:</span>
                      <span className="text-white">{pendingFlightPopup.parsed_data.flight_date}</span>
                    </div>
                  )}
                  {(pendingFlightPopup.parsed_data.origin || pendingFlightPopup.parsed_data.destination) && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">Route:</span>
                      <span className="text-white">{pendingFlightPopup.parsed_data.origin} → {pendingFlightPopup.parsed_data.destination}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="p-4 border-t border-gray-700 flex items-center gap-3">
              <Button 
                onClick={openFlightRequests}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold"
                data-testid="flight-popup-open-btn"
              >
                <Plane size={18} className="mr-2" />
                Review Request
              </Button>
              <Button 
                onClick={dismissFlightPopup}
                variant="outline"
                className="border-gray-600 text-gray-400 hover:bg-gray-800"
                data-testid="flight-popup-dismiss-btn"
              >
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Global demo-mode popup for Exposition (visual-only) users */}
      <DemoModeDialog />

      {/* Password expiry warning popup */}
      {showPwWarning && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="pw-warning-overlay">
          <div className="bg-[#0f172a] border-2 border-amber-500 rounded-xl shadow-2xl shadow-amber-500/20 max-w-md w-full mx-4 overflow-hidden animate-in fade-in zoom-in" data-testid="pw-warning-popup">
            <div className="p-4 bg-amber-500">
              <div className="flex items-center gap-3">
                <AlertTriangle size={28} className="text-[#0f172a]" />
                <div>
                  <h2 className="text-[#0f172a] font-bold text-lg">Password Expiring Soon</h2>
                  <p className="text-[#0f172a]/80 text-sm">Security policy — 90 day rotation</p>
                </div>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-white text-sm">
                Your password will expire in{" "}
                <span className="font-bold text-amber-400" data-testid="pw-warning-days">
                  {user?.password_days_left != null ? Math.max(0, user.password_days_left) : "a few"} day
                  {user?.password_days_left === 1 ? "" : "s"}
                </span>
                . Please change it now to avoid being locked out and having to reset it by email.
              </p>
            </div>
            <div className="p-4 border-t border-gray-700 flex items-center gap-3">
              <Button
                onClick={() => { dismissPwWarning(); navigate("/settings"); }}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-[#0f172a] font-bold"
                data-testid="pw-warning-change-btn"
              >
                Change Password Now
              </Button>
              <Button
                onClick={dismissPwWarning}
                variant="outline"
                className="border-gray-600 text-gray-400 hover:bg-gray-800"
                data-testid="pw-warning-later-btn"
              >
                Remind Me Later
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
